import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";
import { ApiError } from "./documentsApi";

/**
 * Review-before-push flow for PDF bank/credit statements.
 *
 * Upload queues a `reducto_extraction` job; the worker
 * (GET /runReductoProcess) runs a sync Reducto deep extract (73–274s),
 * lands the statement in status `awaiting_approval`, and the user
 * approves/rejects from the review screen. Only approval pushes to Zoho.
 */

/**
 * How a row was graded against what is already imported (ZOUR-87).
 *
 *  exact     same date + amount + narration, or the same bank reference
 *  probable  same date + amount, narration similar above the threshold
 *  same_da   same date + amount, narration clearly different (usually a real
 *            second transaction, so it stays ticked)
 *  infile    an identical row appears earlier in this same upload
 *  null      nothing matched
 */
export type MatchTier = "exact" | "probable" | "same_da" | "infile";

/** The already-imported (or earlier-in-file) row a flagged row matched. */
export interface ReviewMatch {
  date: string;
  amount: number;
  description: string;
  reference_no: string | null;
  balance: number | null;
  /** Which side of the ledger the matched row sits on; null when not recorded. */
  side: "debit" | "credit" | null;
  statement_id: number | null;
  imported_on: string | null;
  /** `imported` = already in the books; `this_file` = earlier in this upload */
  source: "imported" | "this_file";
  tier: MatchTier;
}

export interface ReviewTransaction {
  transaction_id: string;
  date: string; // Y-m-d
  debit_or_credit: "debit" | "credit";
  amount: number;
  payee: string;
  description: string;
  balance: number | null;
  /** Bank's own reference (UTR/UPI/cheque) — a ZOUR-87 match signal. */
  reference_no?: string | null;
  /** Set by GET review/{id}: row already imported for this bank account */
  is_duplicate?: boolean;
  /** ZOUR-87 grade. Absent on an older backend, which reads as "clean". */
  tier?: MatchTier | null;
  similarity?: number | null;
  match?: ReviewMatch | null;
  /** What the tier alone would have chosen, before the reviewer touched it. */
  import_default?: boolean;
  /**
   * The reviewer's decision. Sent back on approve and OBEYED: a ticked row is
   * pushed even when graded a duplicate, an unticked one never is.
   */
  import?: boolean;
}

/** Rows on each tier, as the review header counts them. */
export interface TierCounts {
  exact: number;
  probable: number;
  same_da: number;
  infile: number;
  clean: number;
}

export interface ReviewSummary {
  transaction_count: number;
  debit_count: number;
  credit_count: number;
  total_debit: number;
  total_credit: number;
  opening_balance: number | null;
  closing_balance: number | null;
  balance_chain: "verified" | "broken" | "unavailable";
  balance_chain_breaks: Array<{
    index: number;
    date: string;
    expected: number;
    printed: number;
  }>;
  duplicate_count?: number;
  rows_from_reducto?: number;
  rows_skipped_in_normalization?: number;
  /** ZOUR-87. Absent on an older backend. */
  tier_counts?: TierCounts;
  import_count?: number;
  duplicate_match_threshold?: number;
}

/** Where the approved rows will land — shown in the review summary area. */
export interface ReviewBankAccount {
  id: number;
  zoho_bankaccountid: string;
  bank_name: string | null;
  account_number_masked: string | null;
  account_type: string | null;
  currency_code: string | null;
}

export interface StatementReview {
  id: number;
  job_id: string;
  filename: string;
  statement_type: "bank" | "credit";
  bank_name: string | null;
  zoho_bankaccountid: string | null;
  bank_account: ReviewBankAccount | null;
  status: string;
  extraction_engine: string | null;
  /** ZOUR-87 similarity threshold this payload was graded with. */
  duplicate_match_threshold?: number;
  summary: ReviewSummary | null;
  transactions: ReviewTransaction[];
  pdf_url: string | false;
  reducto_credits: string | number | null;
  reducto_cost_usd: string | number | null;
  extraction_duration_ms: number | null;
  extraction_attempt: number | null;
  attempt_count: number;
  last_error: string | null;
  job_status: string | null;
}

export interface StatementStatus {
  id: number;
  job_id: string;
  status: string;
  textract_status: string;
  queue_status?: string;
  queue_job_type?: string;
  queue_attempts?: number;
  queue_max_attempts?: number;
  queue_created_at?: string;
  queue_updated_at?: string;
  error_message?: string;
  overall_status?: string;
}

/** Retry becomes available once a job has been stuck this long. */
export const STUCK_JOB_MINUTES = 15;

const authHeaders = (): Record<string, string> => {
  const token = getToken();
  if (!token) {
    throw new ApiError("No authentication token found", 401);
  }
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

/**
 * Fire-and-forget kick of the Reducto worker.
 *
 * BACKUP ONLY: the backend already fires its own in-container self-call when
 * the upload enqueues the job (see WorkerKicker), so the extraction starts
 * even if the browser never sends this. Kept as belt-and-braces. The worker
 * sets ignore_user_abort — we deliberately don't await the minutes-long
 * response.
 */
export const kickReductoWorker = (): void => {
  fetch(`${API_BASE_URL}/runReductoProcess`, { method: "GET" }).catch(() => {
    // The backend kick is the primary trigger; a dropped kick is not fatal.
  });
};

/** Same fire-and-forget kick for the legacy Textract worker (after reject). */
export const kickLegacyStatementWorker = (): void => {
  fetch(`${API_BASE_URL}/runStatementProcess`, { method: "GET" }).catch(() => {
    // Cron will pick the job up.
  });
};

/** Poll the extraction state for a job. */
export const fetchStatementStatusApi = async (
  jobId: string
): Promise<StatementStatus> => {
  const response = await fetch(
    `${API_BASE_URL}/api/data/statement-status/${encodeURIComponent(jobId)}`,
    { headers: authHeaders() }
  );
  const data = await response.json();
  if (!response.ok || data.success === false) {
    throw new ApiError(
      data.message || "Failed to fetch statement status",
      response.status,
      data
    );
  }
  return data.data;
};

/** Full review payload: summary, transactions, signed PDF URL. */
export const fetchStatementReviewApi = async (
  id: number
): Promise<StatementReview> => {
  const response = await fetch(
    `${API_BASE_URL}/api/statement-processing/review/${id}`,
    { headers: authHeaders() }
  );
  const data = await response.json();
  if (!response.ok || data.success === false) {
    throw new ApiError(
      data.message || "Failed to fetch statement review",
      response.status,
      data
    );
  }
  return data.data;
};

export interface ApproveResult {
  statement_id: number;
  processed_transaction_count: number;
  duplicate_transaction_count: number;
  /** ZOUR-87: ticked rows this same statement had already imported. */
  refused_transaction_count?: number;
  tier_counts?: TierCounts;
  duplicate_match_threshold?: number;
  sync_status: "success" | "failed";
  sync_message: string;
}

/**
 * Approve the reviewed extraction and push it into the existing pipeline
 * (dedup -> Zoho import -> statements/transactions -> bank sync).
 * Pass the table's rows so user edits are what actually gets pushed.
 *
 * ZOUR-87: every row carries `import`. Send ALL rows, not just the ticked
 * ones — the unticked rows are what the backend records as the audit trail of
 * what the reviewer chose to leave out.
 */
export const approveStatementReviewApi = async (
  id: number,
  transactions: ReviewTransaction[]
): Promise<ApproveResult> => {
  const response = await fetch(
    `${API_BASE_URL}/api/statement-processing/review/${id}/approve`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ transactions }),
    }
  );
  const data = await response.json();
  if (!response.ok || data.success === false) {
    throw new ApiError(
      data.message || "Failed to approve statement",
      response.status,
      data
    );
  }
  return data.data;
};

export interface RetryResult {
  id: number;
  job_id: string;
  job_status: string;
  status: string;
  textract_status: string;
  worker_kicked: boolean;
}

/**
 * Manually re-run a failed or stuck extraction. Manual only by design —
 * every Reducto run bills real money, so nothing retries by itself.
 */
export const retryStatementExtractionApi = async (
  id: number
): Promise<RetryResult> => {
  const response = await fetch(
    `${API_BASE_URL}/api/statement-processing/review/${id}/retry`,
    { method: "POST", headers: authHeaders() }
  );
  const data = await response.json();
  if (!response.ok || data.success === false) {
    throw new ApiError(
      data.message || "Failed to retry extraction",
      response.status,
      data
    );
  }
  // The backend already kicks the worker; this is the belt-and-braces backup.
  kickReductoWorker();
  return data.data;
};

/** Reject the extraction — the statement is requeued for the legacy flow. */
export const rejectStatementReviewApi = async (id: number): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/api/statement-processing/review/${id}/reject`,
    { method: "POST", headers: authHeaders() }
  );
  const data = await response.json();
  if (!response.ok || data.success === false) {
    throw new ApiError(
      data.message || "Failed to reject statement",
      response.status,
      data
    );
  }
  kickLegacyStatementWorker();
};

/**
 * Explicit, user-triggered hand-off of a rejected statement (or a failed
 * extraction) to the legacy Textract flow. Deliberately separate from
 * Reject: the legacy flow extracts AND posts to Zoho automatically with no
 * review screen, so entering it must be a conscious choice.
 */
export const processStatementViaTextractApi = async (
  id: number
): Promise<{ job_id: string; worker_kicked: boolean }> => {
  const response = await fetch(
    `${API_BASE_URL}/api/statement-processing/review/${id}/textract`,
    { method: "POST", headers: authHeaders() }
  );
  const data = await response.json();
  if (!response.ok || data.success === false) {
    throw new ApiError(
      data.message || "Failed to queue the standard extraction",
      response.status,
      data
    );
  }
  // The backend already kicks the legacy worker; belt-and-braces backup.
  kickLegacyStatementWorker();
  return data.data;
};
